/**
 * Subagent spawn CTA row (ticket 3525, upstream pin e3e7cc3f, MIT), ported
 * from upstream `MessagesTimeline.tsx`. Forge adaptation: there is no
 * agents-panel model yet (a later port ticket), so member statuses are
 * approximated from the batch lifecycle instead of the per-agent roster.
 *
 * @module TimelineSpawnRow
 */

import { use } from "react";
import { cn } from "~/lib/utils";
import type { WorkLogEntry } from "../../session-logic";
import { deriveAgentSpawnSummary, type SpawnAgentView } from "./agentSpawnSummary";
import { LiveActivityRow } from "./TimelineLiveActivityRow";
import { toolCallExpandedBodyClassName } from "./TimelineWorkEntryRows";
import { TimelineRowCtx } from "./TimelineRowContext";

/**
 * One tool row per subagent batch, with the member list available on expansion
 * (ticket 3525, upstream `AgentSpawnRow`).
 *
 * Forge adaptation: there is no agents-panel model yet (a later port ticket),
 * so member statuses are approximated from the spawn entry's own lifecycle
 * (live while the batch runs, failed if it ended in failure) instead of the
 * per-agent roster, and the "Open Agents panel" link waits for that ticket.
 */
export function AgentSpawnRow(props: {
  workEntry: WorkLogEntry;
  active?: boolean | undefined;
  onToggleEntry?: ((collapsed: boolean) => void) | undefined;
}) {
  const { workEntry } = props;
  const ctx = use(TimelineRowCtx);
  const spawn = workEntry.agentSpawn;

  if (!spawn) {
    return null;
  }
  const expanded = ctx.expandedSpawnEntryIds.has(workEntry.id);
  const failed = workEntryDisplayFailed(workEntry);
  const activeStatus = props.active !== false && !failed;
  const memberStatus: SpawnAgentView["status"] = failed
    ? "failed"
    : activeStatus
      ? "running"
      : "completed";
  const members: SpawnAgentView[] = spawn.agentTaskIds.map((taskId) =>
    taskId === spawn.workflowId
      ? { kind: "workflow", status: memberStatus }
      : { kind: "subagent", status: memberStatus },
  );
  const agentCount = spawn.workflowId ? Math.max(0, members.length - 1) : members.length;
  const summary = deriveAgentSpawnSummary({
    agents: members,
    agentCount,
  });
  const toggleExpanded = () => {
    props.onToggleEntry?.(expanded);
    ctx.onToggleSpawnRow(workEntry.id, !expanded);
  };

  return (
    <div className="flex flex-col">
      <button
        type="button"
        aria-expanded={expanded}
        onClick={toggleExpanded}
        className="flex cursor-pointer select-none rounded-md text-left transition-colors hover:bg-accent/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/70"
      >
        <LiveActivityRow
          label={summary.lead}
          iconName="bot"
          active={summary.live && props.active !== false}
          failed={failed}
        />
      </button>
      {expanded ? (
        <div className="ms-7 mt-0.5 flex flex-col">
          <div className="flex select-none items-center gap-1.5 px-1">
            <p className="min-w-0 flex-1 text-sm leading-relaxed">
              <span
                className={cn(
                  "truncate",
                  failed ? "text-destructive-foreground" : "text-foreground/80",
                )}
              >
                {summary.status}
              </span>
              {workEntry.agentRole ? (
                <span className="ms-2 rounded-sm border border-border/60 px-1 font-mono text-3xs text-muted-foreground">
                  {workEntry.agentRole}
                </span>
              ) : null}
            </p>
          </div>
          {workEntry.detail ? (
            <div className="mt-1 cursor-default rounded-md bg-muted/40 px-3 py-2">
              <pre className={toolCallExpandedBodyClassName}>{workEntry.detail}</pre>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function workEntryDisplayFailed(entry: WorkLogEntry): boolean {
  return (
    entry.tone === "error" ||
    entry.toolLifecycleStatus === "failed" ||
    entry.toolLifecycleStatus === "declined"
  );
}
